import { fetchApi, parseError } from '@/api/http'
import { isAdmin } from '@/utils/adminAuth'
import { isProfissional } from '@/utils/profissionalAuth'
import { forceRelogin } from '@/utils/authFlow'

export type TarefaStatus = 'pendente' | 'em_progresso' | 'concluida'

export interface TarefaCategoria {
  id: number
  nome: string
  cor: string | null
  ordem: number
  ativo: number | boolean
  criado_em?: string
}

export interface Tarefa {
  id: number
  categoria_id: number | null
  nome: string
  descricao: string | null
  data_tarefa: string
  hora: string | null
  status: TarefaStatus
  criado_por_tipo: 'admin' | 'profissional'
  criado_por_id: number | null
  criado_em?: string
  atualizado_em?: string
  categoria_nome?: string | null
  categoria_cor?: string | null
}

export interface TarefaConfig {
  mostrar_concluidas: boolean
  exigir_categoria: boolean
}

export interface TarefaInput {
  nome: string
  descricao?: string | null
  data_tarefa: string
  hora?: string | null
  status?: TarefaStatus
  categoria_id?: number | null
}

export interface TarefaCategoriaInput {
  nome: string
  cor?: string | null
  ordem?: number
  ativo?: boolean
}

async function handleStaffResponse<T>(res: Response): Promise<T> {
  const text = await res.text()
  if (res.status === 401 || res.status === 403) {
    const msg = (() => {
      try {
        const d = JSON.parse(text) as { error?: string }
        return d?.error ?? 'Sessão inválida ou expirada. Faça login novamente.'
      } catch {
        return 'Sessão inválida ou expirada. Faça login novamente.'
      }
    })()
    if (isAdmin()) forceRelogin('admin', msg)
    if (isProfissional()) forceRelogin('profissional', msg)
    throw new Error(msg)
  }
  if (!res.ok) return parseError(res, text)
  if (!text) return undefined as T
  return JSON.parse(text) as T
}

/* ——— Config ——— */
export async function getTarefaConfig(): Promise<TarefaConfig> {
  const res = await fetchApi('/api/tarefas/settings')
  return handleStaffResponse(res)
}

export async function putTarefaConfig(data: TarefaConfig): Promise<TarefaConfig> {
  const res = await fetchApi('/api/tarefas/settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return handleStaffResponse(res)
}

/* ——— Categorias ——— */
export async function getTarefaCategorias(incluirInativas = false): Promise<TarefaCategoria[]> {
  const q = incluirInativas ? '?ativo=false' : ''
  const res = await fetchApi(`/api/tarefas/categorias${q}`)
  return handleStaffResponse(res)
}

export async function createTarefaCategoria(data: TarefaCategoriaInput): Promise<TarefaCategoria> {
  const res = await fetchApi('/api/tarefas/categorias', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return handleStaffResponse(res)
}

export async function updateTarefaCategoria(
  id: number,
  data: Partial<TarefaCategoriaInput>
): Promise<TarefaCategoria> {
  const res = await fetchApi(`/api/tarefas/categorias/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return handleStaffResponse(res)
}

export async function deleteTarefaCategoria(id: number): Promise<void> {
  const res = await fetchApi(`/api/tarefas/categorias/${id}`, { method: 'DELETE' })
  await handleStaffResponse(res)
}

/* ——— Tarefas ——— */
export interface ListTarefasParams {
  data?: string
  de?: string
  ate?: string
  categoria_id?: number
  status?: TarefaStatus
}

export async function getTarefas(params: ListTarefasParams = {}): Promise<Tarefa[]> {
  const q = new URLSearchParams()
  if (params.data) q.set('data', params.data)
  if (params.de) q.set('de', params.de)
  if (params.ate) q.set('ate', params.ate)
  if (params.categoria_id != null) q.set('categoria_id', String(params.categoria_id))
  if (params.status) q.set('status', params.status)
  const qs = q.toString()
  const res = await fetchApi(`/api/tarefas${qs ? `?${qs}` : ''}`)
  return handleStaffResponse(res)
}

export async function createTarefa(data: TarefaInput): Promise<Tarefa> {
  const res = await fetchApi('/api/tarefas', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return handleStaffResponse(res)
}

export async function updateTarefa(id: number, data: Partial<TarefaInput>): Promise<Tarefa> {
  const res = await fetchApi(`/api/tarefas/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return handleStaffResponse(res)
}

export async function deleteTarefa(id: number): Promise<void> {
  const res = await fetchApi(`/api/tarefas/${id}`, { method: 'DELETE' })
  await handleStaffResponse(res)
}
